import { useState } from 'react'
import type Activity from '../../model/Activity'
import { capRmsAtNextGate } from '../../logic/gateUtils'
import {
  gateCutWarning,
  getFixedLabel,
  getQuickAmounts,
} from '../../hooks/log/logUtils'
import AppIcon from '../icons/AppIcon'
import ActivityIcon from '../icons/ActivityIcon'
import Button from '../primitives/Button'
import Keypad, { QuickAmountChips } from '../primitives/Keypad'
import styles from './LogScreen.module.css'

interface AmountEntryProps {
  activity: Activity
  gateRoom: number | null
  onBack: () => void
  onSubmit: (value: number) => void
}

export default function AmountEntry({ activity, gateRoom, onBack, onSubmit }: AmountEntryProps) {
  const [amount, onAmountChange] = useState('')
  const isFixed = !activity.requiresAmount
  const numericValue = parseFloat(amount) || 0
  const proposedRms = isFixed ? activity.unitConversion : numericValue * activity.unitConversion
  const { creditedRms, cutRms } = capRmsAtNextGate(proposedRms, gateRoom)
  const rms = Math.round(creditedRms)
  const gateWarning = gateCutWarning(cutRms, creditedRms)
  const canSubmit = isFixed ? true : numericValue > 0

  function press(key: string) {
    if (isFixed) return
    let next = amount
    if (key === '.') {
      if (next.includes('.')) return
      if (next === '') next = '0'
    }
    if (next.replace('.', '').length >= 5) return
    next += key
    onAmountChange(next)
  }

  function backspace() {
    if (isFixed) return
    onAmountChange(amount.slice(0, -1))
  }

  function setAmount(value: number) {
    onAmountChange(String(value))
  }

  const displayAmount = isFixed ? getFixedLabel(activity) : amount === '' ? '0' : amount
  const isEmpty = !isFixed && (amount === '' || numericValue === 0)

  return (
    <div className={[styles.screen, isFixed && styles.screenFixed].filter(Boolean).join(' ')}>
      <div className={styles.head}>
        <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Back">
          <AppIcon icon="mdi:chevron-left" size={22} />
        </button>
      </div>

      <div className={styles.hero}>
        <div className={styles.iconWrap}>
          <ActivityIcon activity={activity} size={40} />
        </div>
        <div>
          <div className={styles.name}>{activity.title}</div>
          <div className={styles.conv}>{activity.description}</div>
        </div>
      </div>

      <div className={styles.display}>
        <div className={[styles.amount, isEmpty && styles.empty].filter(Boolean).join(' ')}>
          <span data-testid="amount">{displayAmount}</span>
          {!isFixed && activity.unit && <span className={styles.unit}>{activity.unit}</span>}
        </div>
        <div className={styles.rmBlock}>
          <div className={styles.rmOut}>
            <span className={styles.eq}>=</span>
            <span className={styles.rmVal} data-testid="rms-earned">
              {rms}
            </span>
            <span className={styles.rmLbl}>RMs earned</span>
          </div>
          <div className={styles.gateCutSlot}>
            {gateWarning && (
              <p className={styles.gateCut} data-testid="gate-cut-warning">
                {gateWarning}
              </p>
            )}
          </div>
        </div>
      </div>

      {!isFixed && (
        <>
          <QuickAmountChips values={getQuickAmounts(activity)} onSelect={setAmount} />
          <Keypad onPress={press} onBackspace={backspace} />
        </>
      )}

      <div className={styles.footer}>
        <Button onClick={() => onSubmit(isFixed ? 1 : numericValue)} disabled={!canSubmit}>
          Log it
          <AppIcon icon="mdi:arrow-right" size={18} />
        </Button>
      </div>
    </div>
  )
}
